import React from 'react';
import { useField } from 'formik';
import { useSelector } from 'react-redux';

import { Q1Value } from 'models/documents';
import { UserDecimalSep } from 'models/user';
import { RootState } from 'models';

import utils from 'utils';
import styled from 'styled-components/macro';
import selectors from 'state/selectors';
import { actionType } from 'utils/constants';

import { DataGridRow } from 'components/DataGrid';
import NumberInput from 'components/Input/NumberInput';
import { BoundingBox } from 'models/response';

export interface CellTextInput {
  name: string;
  focus: boolean;
  onFocus: (cell?: { pageNumber: number; boundingBox: BoundingBox; content: string }) => void;
  row: DataGridRow;
  code: number;
  decimalSeparator: UserDecimalSep | false;
  hasFixedDecimals?: boolean;
  disabled?: boolean;
}

const GridInput = styled.input`
  max-width: 100%;
  font-family: ${({ theme }) => theme.fontFamily};
  font-weight: ${({ theme }) => theme.fontWeightPalette.regular};
  border: none;
  border-radius: 3px;
  &:focus {
    outline: 0;
  }
  ::-ms-reveal,
  ::-ms-clear {
    display: none;
  }
`;

const CellTextInput = ({
  name,
  focus,
  onFocus,
  row,
  code,
  decimalSeparator,
  hasFixedDecimals = false,
  disabled,
}: CellTextInput) => {
  const [field, , helpers] = useField<Q1Value>(name);
  const config = useSelector((store: RootState) =>
    selectors.app.getConfig(store, actionType.CLEAR_ZERO_VALUE_Q3_CURRENCY_FIELD),
  );
  const decimalScale = useSelector(selectors.documents.selectDocumentDecimal);

  // Formatting and updating the rows state on inputs change
  const onFieldChange = (value: number | undefined) => {
    const body: DataGridRow[] | undefined = field.value.body?.map((elem: DataGridRow) => {
      const content = value === undefined ? '' : utils.intl.formatNumber(value, decimalScale, ',');
      if (elem.id === row.id) {
        return {
          ...elem,
          [code]: {
            content,
            pageNumber: -1,
            boundingBox: {
              x1: -1,
              y1: -1,
              x2: -1,
              y2: -1,
            },
          },
        };
      }
      return elem;
    });
    if (body) helpers.setValue({ ...field.value, body });
  };

  const onBlur = (value: number | undefined) => {
    if (value === undefined) return;
    if (config.val === 1 && value === 0) onFieldChange(undefined);
  };

  let value: number | undefined;
  const fieldValue = field.value.body?.find(({ id }) => id === row.id);
  if (fieldValue) {
    value = fieldValue[code]?.content && utils.intl.convertToNumber(fieldValue[code].content);
  }

  return (
    <NumberInput
      customInput={GridInput}
      value={value}
      focus={focus}
      onFocus={() => onFocus(fieldValue ? fieldValue[code] : undefined)}
      onBlur={onBlur}
      decimalScale={decimalScale}
      decimalSeparator={decimalSeparator}
      hasFixedDecimals={hasFixedDecimals}
      onChange={onFieldChange}
      readOnly={disabled}
    />
  );
};

export default CellTextInput;
